import { CaseEvent, EventType } from "../../../api/events";
import { createEntityAdapter, createSlice, EntityState } from "@reduxjs/toolkit";
import { addCaseEvents } from "./thunks";
import { RootState } from "../../../app/store";
import { savePreSCNFicMeetings } from "../../reporting/components/preScnFIC/logic/PreSCNFicThunk";
import { saveAssessments } from "../../reporting/components/ObservationsByAuthority/logic/ObservationsByAuthorityThunk";
import { createFinalFICMeetingThunk, updateFinalFICMeetingThunk } from "../../reporting/ficDecision/logic/thunks";
import { updateCaseStage } from "../../caseStages/logic/thunks";
import { fetchCaseInfo } from "../../../thunks/Case";

interface CaseEventsState {
  caseId?: string;
    status: 'idle' | 'loading' | 'success' | 'error';
  events: EntityState<CaseEvent, string>;
}

const caseEventsAdapter = createEntityAdapter<CaseEvent, string>({
    selectId: (event) => event.event_id
})

const initialState: CaseEventsState = {
    status: 'idle',
    events: caseEventsAdapter.getInitialState({})
}

const caseEventsSlice = createSlice({
  name: "caseEvents",
  initialState,
  reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchCaseInfo.pending, (state, action) => {
            state.caseId = action.meta.arg.case_id;
            state.status = 'loading';
        });
        builder.addCase(fetchCaseInfo.rejected, (state, action) => {
            state.caseId = action.meta.arg.case_id;
            state.status = 'error';
        });
        builder.addCase(fetchCaseInfo.fulfilled, (state, action) => {
            const caseData = action.payload.cases?.[0];
            state.caseId = caseData?.case_id ?? action.meta.arg.case_id;
            state.status = 'success';
            caseEventsAdapter.setAll(state.events, caseData?.events ?? []);
        });

        builder.addCase(addCaseEvents.fulfilled, (state, action) => {
            caseEventsAdapter.addMany(state.events, action.payload.events);
        })

        builder.addCase(savePreSCNFicMeetings.fulfilled, (state, action) => {
            if (action.payload.events?.length) {
                caseEventsAdapter.upsertMany(state.events, action.payload.events);
            }
        });
        builder.addCase(saveAssessments.fulfilled, (state, action) => {
            if (action.payload.events?.length) {
                caseEventsAdapter.upsertMany(state.events, action.payload.events);
            }
        });
        builder.addCase(createFinalFICMeetingThunk.fulfilled, (state, action) => {
            if (action.payload.events?.length) {
                caseEventsAdapter.upsertMany(state.events, action.payload.events);
            }
        });
        builder.addCase(updateFinalFICMeetingThunk.fulfilled, (state, action) => {
            const events = action.meta.arg.request.events;
            if (events?.length) {
                caseEventsAdapter.upsertMany(state.events, events);
            }
        });
        builder.addCase(updateCaseStage.fulfilled, (state, action) => {
            const events = action.payload?.events;
            if (events?.length) {
                caseEventsAdapter.addMany(state.events, events);
            }
        });
    }
})

export const { selectAll:selectAllEvents } = caseEventsAdapter.getSelectors((state: RootState) => state.eventsData.events)

export const selectEventByTypeAndEntityId = (eventType: EventType, entity_id: string) => {
  return (state: RootState) => {
    const allEvents = selectAllEvents(state);
    return allEvents.find((event) => (event.event_type===eventType && event.entity_id === entity_id));
  }
}

export const isEventsDataLoading = (state: RootState) => state.eventsData.status==='loading';
export const isEventsDataFailed = (state: RootState) => state.eventsData.status==='error';

export const caseEventsReducer = caseEventsSlice.reducer;